import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, Text, StyleSheet, ScrollView, Image, Pressable, ActivityIndicator, KeyboardAvoidingView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import Modal from 'react-native-modal';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import SendMessageModal from '../SendMessageModal';
import TicketActivity from '../TicketActivity';
import CommentsPane from '../CommentsPane';
import ScreenEmptyState from '../../../../components/ScreenEmptyState';
import NoSelectedTicketImage from '../NoSelectedTicketImage';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import NoteIcon from '../../../../components/NoteIcon';
import ClosureModal from '../ClosureModal';
import PressableStyled from '../../../../components/PressableStyled';
import PriorityIcon from '../../../../components/PriorityIcon';
import ProfileIcon from '../../../../components/24pxIcons/ProfileIcon';
import TopRightIcon from '../../../../components/12pxIcons/TopRightIcon';
import isMobile from '../../../../components/IsMobile';
import TicketPrioritySelector from '../TicketPrioritySelector';
import { logEvent } from '../../../../components/LogEvent';
import ZoomIcon from '../../../../components/24pxIcons/ZoomIcon';
import TimeSection from './components/TimeSection';
import {
  ActivitySection, AssetInstance,
  DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, TicketStatus, UserGroup,
  UserInstance, UserPermission,
} from '../../../../constants/Types';
import AssetIcon from '../../../../components/AssetIcon';
import TemplateIcon30 from '../../../../components/30pxIcons/TemplateIcon30';
import TicketAssigneeIcon from '../TicketAssigneeIcon';
import TicketStatusButtons from '../TicketStatusButtons';
import { AppScreen } from '../../../../navigation/LinkingConfiguration';
import TicketAssignee from '../TicketAssignee';
import LocationIcon14 from '../../../../components/Map/LocationIcon14';
import MessageReporterPane from '../MessageReporterPane';
import TicketBrief from '../TicketBrief';
import TicketUserGroups from '../TicketUserGroups';
import MyGroupsIcon from '../../../../components/24pxIcons/MyGroupsIcon';

interface selectedTicketWebProps {
  selectedTicket: Ticket,
  users: UserInstance[],
  renderSharePane,
  moreOptionsVisible: boolean,
  renderMoreOptions,
  selectedBuilding: string,
  imageModalVisible: boolean,
  setImageModalVisible: Dispatch<SetStateAction<boolean>>,
  ticketActivityRef,
  name: string,
  closureModalVisible: boolean,
  setClosureModalVisible: Dispatch<SetStateAction<boolean>>,
  resolve: () => void,
  showSpinner: boolean,
  hide?: () => void,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  showAlert,
  userGroups: UserGroup[],
  asset: AssetInstance | null,
  updateSelectedTicketInList,
}

const SelectedTicket = ({
  selectedTicket, users, renderSharePane, moreOptionsVisible, resolve, showAlert, asset, setLoading,
  renderMoreOptions, imageModalVisible, setImageModalVisible, name, setShowSpinner, userGroups,
  showSpinner, closureModalVisible, setClosureModalVisible, hide, ticketActivityRef,
  updateSelectedTicketInList,
}: selectedTicketWebProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  // const isDev: boolean = useSelector((state:
  // { devState: { isDev: boolean } }) => state.devState.isDev);
  const canUpdateTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE);
  const canViewAssets: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_READ);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState<boolean>(false);
  const [messageModalShown, setMessageModalShown] = useState<boolean>(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState<boolean>(false);
  const [assigneeSearchValue, setAssigneeSearchValue] = useState<string>('');
  const [activeTab, setActiveTab] = useState<ActivitySection>(ActivitySection.ALL);
  const [focusEditor, setFocusEditor] = useState<boolean>(false);
  const [savingTicketUserGroups, setSavingTicketUserGroups] = useState<boolean>(false);
  const navigation = useNavigation();
  const scrollRef = React.useRef<ScrollView>(null);

  useEffect(() => {
    setShowPriorityDropdown(false);
    setShowAssigneeDropdown(false);
  }, [selectedTicket]);

  if (selectedTicket) {
    let reporterName: string | null = selectedTicket.reporterName?.trim();
    if (!reporterName) {
      if (selectedTicket.reporter) reporterName = selectedTicket.reporter?.trim();
      else if (selectedTicket.reporterEmail) reporterName = selectedTicket.reporterEmail?.trim();
    }
    let { priority } = selectedTicket;
    if (!ticketPriorityIsValid(priority)) priority = selectedTicket.priority;
    if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
    const { id } = selectedTicket;
    const uri: string = selectedTicket.images[0]?.filename;
    // const uri: string = selectedTicket.images[0]?.filename
    //   ?.replace(/[~`!@#$%^&*()+={};'"<>,?_]/g, ''); // TODO: do we need to strip these chars?
    const locationParts: string[] = [];
    if (selectedTicket.floorName) {
      locationParts.push(selectedTicket.floorName);
    }
    if (selectedTicket.locationName && selectedTicket.locationName !== selectedTicket.floorName) {
      locationParts.push(selectedTicket.locationName);
    }

    const navigateToAsset: () => void = () => {
      logEvent('SelectedTicket', 'go_to_asset');
      if (isMobile && hide) hide();
      if (isMobile) {
        navigation.navigate('AssetStack' as any, {
          assetId: selectedTicket.assetId ?? '-',
          templateId: selectedTicket.assetTemplateId,
        });
      } else {
        navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT as any, {
          assetId: selectedTicket.assetId ?? '-',
          templateId: selectedTicket.assetTemplateId,
        });
      }
    };

    const navigateToLocation: () => void = () => {
      logEvent('SelectedTicket', 'go_to_location');
      const onMap = asset?.customFields.some(
        (field) => field.name === 'GeoX' && Number(field.value) > 0,
      );
      if (!onMap && !canUpdateTickets) {
        showAlert('Location not set', 'This asset is not placed on the map.');
        return;
      }
      if (!onMap && isMobile) {
        showAlert('Location not set', 'This asset is not placed on the map. Please use a desktop to set the location.');
        return;
      }
      if (isMobile && hide) hide();
      if (isMobile) {
        navigation.navigate('MapStack' as any, {
          floor: selectedTicket.floorName,
          assetId: selectedTicket.assetId,
          ticketId: selectedTicket.id,
          ticketsView: true,
          ticketDate: selectedTicket.createdOn,
        });
      } else {
        const {
          areaId, spaceId, assetId, id: ticketId, createdOn: ticketDate,
        } = selectedTicket;
        navigation.navigate(AppScreen.MAP as any, {
          floor: selectedTicket.floorName,
          areaId,
          spaceId,
          assetId,
          ticketId,
          ticketsView: onMap,
          ticketDate,
          placeAsset: !onMap && canUpdateTickets,
        });
      }
    };

    const getMapButtonText = () => {
      const hasGeoX = asset?.customFields.some(
        (field) => field.name === 'GeoX' && Number(field.value) > 0,
      );
      if (hasGeoX) {
        return 'View on map';
      }
      if (isMobile) {
        return 'Location not set';
      }
      if (canUpdateTickets) {
        return 'Set location';
      }
      return 'Location not set';
    };

    return <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior='padding'
      testID='ticket-details'
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        ref={scrollRef}>
        <Animatable.View
          style={[styles.container, isMobile && styles.containerMobile]}
          animation={'fadeIn'}
        >
          {renderSharePane()}
          <Text style={styles.ticketTitle} selectable>
            {selectedTicket.name.length > 50
              ? `${selectedTicket.name.slice(0, 49)}...` : selectedTicket.name}</Text>
          <View style={styles.subContainer}>
            <View style={[isMobile && styles.completedTicketHeader]}>
              <Pressable style={{ flex: 1, alignSelf: 'stretch' }} disabled={!isMobile}>
                <Pressable disabled={!isMobile}>
                  {isMobile ? <>
                    <Text style={styles.sectionTitle}>{'Asset'}</Text>
                    <View style={[styles.detailSection, { padding: 10 }]}>
                      <View style={Style.row}>
                        <AssetIcon style={{ marginRight: 8 }} />
                        <Text style={[styles.ticketLocation, { flex: 1 }]} selectable>
                          {`${selectedTicket.assetName}`}
                        </Text>
                      </View>
                      <Text
                        style={[styles.ticketLocation, { marginTop: 10 }]}
                        selectable
                      >
                        {'View asset details'}
                      </Text>
                      {canViewAssets && <PressableStyled
                        onPress={navigateToAsset}
                        testID='ticket-navigate-to-asset-btn'
                        style={{ position: 'absolute', top: 10, right: 12 }}
                      >
                        <TopRightIcon />
                      </PressableStyled>}
                    </View>
                  </> : <View style={[styles.detailSubsection, { flex: 1 }]}>
                    <Text style={styles.sectionTitle}>{'Asset'}</Text>
                    <Pressable
                      style={Style.row}
                      disabled={!isMobile}
                    >
                      <AssetIcon style={{ marginRight: 8 }} />
                      <Text style={[styles.ticketLocation, { flex: 1 }]} selectable>
                        {`${selectedTicket.assetName}`}
                      </Text>
                      {canViewAssets && <PressableStyled
                        onPress={navigateToAsset}
                        testID='ticket-navigate-to-asset-btn'
                        style={styles.assetButton}
                      >
                        <Text
                          style={[
                            styles.ticketLocation,
                            { color: Colors.light.hereworksBlue500, fontSize: 14 },
                          ]}
                          selectable
                        >
                          {'View asset details'}
                        </Text>
                        <TopRightIcon />
                      </PressableStyled>}
                    </Pressable>
                  </View>}
                  <Text style={styles.sectionTitle}>{'Location'}</Text>
                  <Pressable
                    style={[Style.row, { flexWrap: 'wrap' }]}
                    disabled={!isMobile}
                  >
                    <Text style={styles.ticketLocation}>
                      {locationParts.join(', ')}
                    </Text>
                    <PressableStyled
                      onPress={navigateToLocation}
                      testID='ticket-navigate-to-location-btn'
                      style={styles.assetButton}
                    >
                      <Text
                        style={[
                          styles.ticketLocation,
                          { color: Colors.light.hereworksBlue500, fontSize: 14 },
                        ]} selectable>
                        {getMapButtonText()}
                      </Text>
                      <LocationIcon14 />
                    </PressableStyled>
                  </Pressable>
                </Pressable>
                <View style={{ marginBottom: 12 }}>
                  <View>
                    <Text style={styles.sectionTitle}>{'More information:'}</Text>
                    <Text style={styles.description}>{selectedTicket.issueDescription?.length
                      ? selectedTicket.issueDescription : selectedTicket.name}</Text>
                  </View>
                </View>
              </Pressable>
            </View>
            <View style={!isMobile && Style.row}>
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                <TemplateIcon30 />
                <Text style={[styles.descriptionTitle, { marginStart: 8 }]}>
                  {`${selectedTicket.assetTemplateName}`}
                </Text>
              </View>
              <View>
                <View style={styles.sendMessageSection}>
                  <Text style={styles.reportedByText}>
                    {`Reported ${reporterName ? `by ${reporterName}` : 'anonymously'}`}
                  </Text>
                  {<TicketAssigneeIcon title={reporterName || 'Anonymous user'} />}
                </View>
                <PressableStyled
                  style={styles.sendMessageSection}
                  onPress={() => {
                    setActiveTab(ActivitySection.REPORTER);
                    setFocusEditor(true);
                    scrollRef.current?.scrollToEnd({ animated: true });
                  }}
                  testID='send-message-btn'
                  disabled={!canUpdateTickets}
                >
                  <NoteIcon />
                  <Text style={styles.sendMessageTitle}>{'Send a message to reporter'}</Text>
                </PressableStyled>
              </View>
            </View>
            {moreOptionsVisible && <View style={styles.moreOptions}>
              {renderMoreOptions()}
            </View>}
          </View>
          <ScrollView style={styles.scrollview} showsVerticalScrollIndicator={false}>
            <Pressable disabled={!isMobile}>
              <TicketBrief ticketId={selectedTicket.id} />
              <View style={styles.statusSection}>
                <View>
                  <Text style={styles.sectionTitle}>{'Status'}</Text>
                  {canUpdateTickets && <View style={[Style.row, { marginBottom: 16 }]}>
                    <TicketStatusButtons
                      {...{
                        selectedTicket,
                        updateSelectedTicketInList,
                        showSpinner,
                        setShowSpinner,
                        showAlert,
                        setLoading,
                        setClosureModalVisible,
                      }}
                    />
                  </View>}
                  <View style={[isMobile && styles.detailSection]}>
                    <View style={[
                      !isMobile && Style.row, styles.detailSubsection, styles.prioritySection,
                    ]}>
                      <View style={styles.sectionHeader}>
                        <PriorityIcon color={Colors.light.hereworksBlack400} />
                        <Text style={styles.propertyTitle}>{'Priority'}</Text>
                      </View>
                      <TicketPrioritySelector
                        {...{
                          id,
                          priority,
                          showPriorityDropdown,
                          setShowPriorityDropdown,
                          setLoading,
                          setShowSpinner,
                          updateSelectedTicketInList,
                        }}
                      />
                    </View>
                    {canUpdateTickets && <View
                      style={[
                        !isMobile && Style.row,
                        styles.assigneeSection,
                        {
                          paddingVertical: 8,
                          paddingHorizontal: isMobile ? 10 : 0,
                          zIndex: 1,
                        },
                      ]}
                      testID='assignee-field'>
                      <View style={styles.sectionHeader}>
                        <MyGroupsIcon color={Colors.light.hereworksBlack400} />
                        <Text style={styles.propertyTitle}>{'User groups'}</Text>
                      </View>
                      <View style={{ flex: 1, alignItems: 'flex-end' }}>
                        <TicketUserGroups
                          {...{
                            ticketId: selectedTicket.id,
                            userGroups,
                            ticketUserGroupIds: selectedTicket.userGroupIds,
                            setSavingTicketUserGroups,
                            onUpdateTicketUserGroups: (updatedTags: string[]) => {
                              updateSelectedTicketInList({
                                ...selectedTicket,
                                userGroupIds: updatedTags,
                              });
                            },
                            ticketResolved: selectedTicket.status === TicketStatus.COMPLETED,
                          }}
                        />
                      </View>
                      {savingTicketUserGroups && <View style={[StyleSheet.absoluteFill, {
                        backgroundColor: `${Colors.light.plainBackground}80`,
                        zIndex: 2,
                        borderRadius: 12,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }]}>
                          <ActivityIndicator size='small' color={Colors.light.hereworksBlue500} />
                        </View>}
                    </View>}
                    {(canUpdateTickets || !!selectedTicket.assignee) && <View
                      style={[
                        !isMobile && Style.row, styles.detailSubsection, styles.assigneeSection,
                      ]}
                      testID='assignee-field'>
                      <View style={styles.sectionHeader}>
                        <ProfileIcon color={Colors.light.hereworksBlack400} />
                        <Text style={styles.propertyTitle}>{'Assignee'}</Text>
                      </View>
                      <TicketAssignee
                        {...{
                          userGroups,
                          users,
                          selectedTicket,
                          showAssigneeDropdown,
                          setShowAssigneeDropdown,
                          assigneeSearchValue,
                          setAssigneeSearchValue,
                          setLoading,
                          setShowSpinner,
                          updateSelectedTicketInList,
                        }}
                      />
                    </View>}
                  </View>
                </View>
              </View>
              {selectedTicket.images?.length ? <>
                <Modal
                  isVisible={imageModalVisible}
                  animationIn={'fadeIn'}
                  animationOut={'fadeOut'}
                >
                  <Pressable
                    onPress={() => setImageModalVisible(!imageModalVisible)}
                    style={{ height: '100%', flex: 1 }}
                  >
                    <Image
                      source={{ uri }}
                      resizeMode={'contain'}
                      style={{ width: undefined, height: '100%' }}
                    />
                  </Pressable>
                </Modal>
                <PressableStyled
                  onPress={() => setImageModalVisible(!imageModalVisible)}
                  testID='ticket-open-picture-btn'
                  style={styles.imageContainer}
                >
                  <Image
                    source={{ uri }}
                    resizeMode={'cover'}
                    style={styles.image}
                  />
                  <ZoomIcon
                    style={{
                      position: 'absolute',
                      top: isMobile ? undefined : 16,
                      right: 16,
                      borderRadius: 3,
                      backgroundColor: `${Colors.light.hereworksBlack300}05`,
                    }}
                  />
                </PressableStyled>
              </> : <></>}
              <TimeSection {...{ selectedTicket }} />
              <SendMessageModal
                {...{
                  updateSelectedTicketInList, messageModalShown, id,
                }}
                hide={() => setMessageModalShown(false)}
              />
              <ClosureModal
                id={selectedTicket.id}
                {...{ resolve }}
                hide={() => setClosureModalVisible(false)}
                isVisible={closureModalVisible}
              />
              <TicketActivity
                {...{ selectedTicket, users }}
                ref={ticketActivityRef}
                sectionSelected={activeTab}
                setSectionSelected={setActiveTab}
              />
            </Pressable>
            {(canUpdateTickets
              && activeTab !== ActivitySection.REPORTER)
              && <CommentsPane {...{ name, updateSelectedTicketInList, id }} />}
            {(canUpdateTickets
              && activeTab === ActivitySection.REPORTER)
              && <MessageReporterPane
                {...{ name, updateSelectedTicketInList, id }}
                focusEditor={focusEditor}
              />}
          </ScrollView>
          <ActivityIndicator
            animating={showSpinner}
            size='large'
            style={Style.activityIndicator}
          />
        </Animatable.View>
      </ScrollView>
    </KeyboardAvoidingView>;
  }

  return <ScreenEmptyState
    image={<NoSelectedTicketImage />}
    title='Choose ticket'
    subtitle='Choose the ticket you want to work with'
  />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
  },
  containerMobile: {
    margin: 20,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite200,
  },
  subContainer: {
    paddingHorizontal: isMobile ? 14 : 24,
  },
  completedTicketHeader: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  ticketTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: isMobile ? 16 : 22,
    lineHeight: isMobile ? 24 : 33,
    color: Colors.light.hereworksBlack600,
    paddingHorizontal: isMobile ? 20 : 24,
    paddingVertical: isMobile ? 12 : 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  ticketLocation: {
    fontSize: isMobile ? 14 : 16,
    lineHeight: isMobile ? 18 : 21,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
    marginVertical: 2,
    marginEnd: 4,
  },
  moreOptions: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
  },
  ticketDetail: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksPrimaryGrey900,
  },
  descriptionTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksBlack400,
  },
  description: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksBlack500,
  },
  imageContainer: {
    marginTop: 16,
    justifyContent: 'center',
  },
  image: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksPrimaryGrey,
    width: '100%',
    height: isMobile ? 100 : 150,
  },
  sendMessageTitle: {
    marginStart: 8,
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue400,
  },
  propertyTitle: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginStart: 8,
  },
  detailSection: {
    paddingHorizontal: isMobile ? undefined : 12,
    paddingVertical: isMobile ? undefined : 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    alignSelf: 'stretch',
  },
  sendMessageSection: {
    flexDirection: 'row',
    alignSelf: isMobile ? undefined : 'flex-end',
    paddingTop: 8,
  },
  reportedByText: {
    fontSize: isMobile ? 14 : 16,
    lineHeight: isMobile ? 18 : 21,
    fontFamily: isMobile ? 'Roboto_400Regular' : 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
    marginHorizontal: isMobile ? undefined : 8,
    flex: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: isMobile ? 8 : 0,
  },
  detailSubsection: isMobile ? {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 12,
  } : {
    height: 40,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  reporter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  assigneeFullName: {
    marginStart: 8,
  },
  statusSection: {
    flexDirection: isMobile ? 'column-reverse' : 'column',
    zIndex: 2,
  },
  scrollview: {
    flex: 1,
    paddingHorizontal: isMobile ? 14 : 24,
    marginHorizontal: 0,
    marginTop: 8,
  },
  prioritySection: {
    zIndex: 2,
  },
  assigneeSection: {
    borderBottomWidth: undefined,
  },
  sectionTitle: {
    marginTop: 8,
    marginBottom: isMobile ? 12 : 8,
    fontSize: isMobile ? 16 : 18,
    lineHeight: isMobile ? 24 : 27,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack500,
  },
  assetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    paddingHorizontal: 6,
  },
});

export default SelectedTicket;
